/**
 * The building blocks every variation type's prompt is written with.
 *
 * This module is the prompt *machinery*: describing reference images,
 * expanding `{{Var}}` placeholders, and the campaign overrides that decide
 * which slot a variation fills. What each type actually says lives with that
 * type, in `variationTypes.js`.
 */

export const formatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

// ---------------------------------------------------------------------------
// Reference image descriptions
//
// A variation references one image (`referenceKey: "IMG1"`) or several
// (`referenceKey: ["IMG1", "IMG2"]`). Prompt builders receive both `visual`
// (the primary reference, unchanged) and `visuals` (every reference, in
// order), and describe them through the helpers below so a single reference
// keeps producing exactly the prompt it did before.
// ---------------------------------------------------------------------------

/** Every reference image of a variation, in order, primary first. */
const referenceVisuals = (data) =>
  (data?.visuals?.length ? data.visuals : [data?.visual]).filter(Boolean);

/** The reference images that have been labeled — the only describable ones. */
export const describedVisuals = (data) =>
  referenceVisuals(data).filter((visual) => !!visual?.labeling?.description);

/**
 * The reference images' descriptions: the bare description for a single
 * reference, a numbered block — so the prompt can tell them apart — for more.
 */
export const referenceDescriptions = (data) => {
  const visuals = describedVisuals(data);

  if (visuals.length <= 1) {
    return visuals[0]?.labeling?.description ?? "";
  }

  return visuals
    .map(
      (visual, idx) =>
        `Reference image ${idx + 1} shows:\n${visual.labeling.description}`,
    )
    .join("\n\n");
};

/** The `I already have …` section introducing the reference descriptions. */
export const keyVisualsSection = (data) => {
  const visuals = describedVisuals(data);

  if (!visuals.length) {
    return "";
  }

  return visuals.length === 1
    ? `I already have one key visual, it shows:\n${referenceDescriptions(data)}`
    : `I already have ${visuals.length} key visuals:\n\n${referenceDescriptions(data)}`;
};

/** The key elements of every reference image, de-duplicated. */
export const referenceKeyElements = (data) =>
  Array.from(
    new Set(
      referenceVisuals(data).flatMap(
        (visual) => visual?.labeling?.keyElements ?? [],
      ),
    ),
  );

// ---------------------------------------------------------------------------
// Image variation prompts
//
// `getVariationPromptTemplate` (at the end of this section) resolves a
// variation's prompt from the two lookup tables below:
//
//   1. `CAMPAIGN_OVERRIDES[templateId][variation.key]` — campaign fields that
//      depend on the slot a variation fills (which persona, banner or scene it
//      renders). Merged over the campaign before the prompt is built.
//   2. `VARIATION_TYPES[variation.type].prompt` — the Zoltar ID/user-message
//      request, or a plain prompt string, built from the merged data.
//
// Both are plain data, so supporting a new template key or variation type is a
// single entry rather than another branch across two nested switches.
// ---------------------------------------------------------------------------

/**
 * A `csc-overview` audience slot: the Nth generated audience — falling back to
 * the first three when fewer were generated — plus the campaign's Nth tagline.
 */
const cscAudienceSlot =
  (audienceIndex, taglineIndex) =>
  ({ campaign }) => ({
    audience:
      campaign?.audiences?.[audienceIndex] ??
      campaign?.audiences?.[audienceIndex % 3],
    tagline: campaign?.taglines?.[taglineIndex]?.en,
  });

/** The Nth persona's audience / visited location. */
const personaAudience =
  (n) =>
  ({ campaign }) => ({ Audience: campaign?.[`Persona-${n}-audience`] });

const personaLocation =
  (n) =>
  ({ campaign }) => ({
    "Location-visited": campaign?.[`Persona-${n}-location`],
  });

/** Feed a campaign field into the differently-named field the prompt reads. */
const campaignField =
  (target, source) =>
  ({ campaign }) => ({ [target]: campaign?.[source] });

/** An `ai` banner visual: that banner's headline/copy plus the reference. */
const aiBannerContext =
  (n) =>
  ({ campaign, visual, visuals }) => ({
    Context: `This image will be used as a banner visual for a campaign with the following details:
Headline: ${campaign?.[`Banner${n}-slogan`]}
Copy: ${campaign?.[`Banner${n}-copy`]}

${describedVisuals({ visual, visuals }).length > 1 ? "The original images show:" : "The original image shows:"}
${referenceDescriptions({ visual, visuals })}`,
  });

const ucxPersona1AudienceCity = ({ campaign }) => ({
  Audience: campaign?.["Persona-1-audience"],
  City: campaign?.["Persona-1-location"],
});

const UCX_CAMPAIGN_OVERRIDES = {
  // UCX-campaign-audience
  "IMG1-V1": ucxPersona1AudienceCity,
  "IMG1-V3": ucxPersona1AudienceCity,

  // UCX-topic-abstract
  "IMG1-V2": campaignField("Campaign-topic", "Form-title"),
  "IMG1-V4": campaignField("Campaign-topic", "Invitation-email-copy"),

  // UCX-location-thumbnail
  "Thumb-1-City": personaLocation(1),
  "Thumb-2-City": personaLocation(2),

  // UCX-audience-thumbnail
  "Thumb-1-Audience": personaAudience(1),
  "Thumb-2-Audience": personaAudience(2),
};

/** `ao-overview` / `cx-coworker` only reuse the first persona's thumbnail. */
const AO_CAMPAIGN_OVERRIDES = {
  // UCX-audience-thumbnail
  "Thumb-1-Audience": personaAudience(1),
};

export const CAMPAIGN_OVERRIDES = {
  "csc-overview": {
    "IMG1-A1": cscAudienceSlot(0, 0),
    "IMG1-A2": cscAudienceSlot(1, 0),
    "IMG1-A3": cscAudienceSlot(2, 0),
    "IMG3-A1": cscAudienceSlot(3, 2),
    "IMG3-A2": cscAudienceSlot(4, 2),
    "IMG3-A3": cscAudienceSlot(5, 2),
  },

  ucx: UCX_CAMPAIGN_OVERRIDES,
  "ucx-vo": UCX_CAMPAIGN_OVERRIDES,

  "ao-overview": AO_CAMPAIGN_OVERRIDES,
  "ao-overview-vo": AO_CAMPAIGN_OVERRIDES,
  "cx-coworker": AO_CAMPAIGN_OVERRIDES,

  foundry: {
    // Each scene variation renders the same-named campaign field.
    "TV-commercial-scene-1": campaignField(
      "TV-commercial-scene",
      "TV-commercial-scene-1",
    ),
    "TV-commercial-scene-2": campaignField(
      "TV-commercial-scene",
      "TV-commercial-scene-2",
    ),
    "TV-commercial-scene-3": campaignField(
      "TV-commercial-scene",
      "TV-commercial-scene-3",
    ),
  },

  ai: {
    "IMG1-V2": aiBannerContext(1),
    "IMG2-V2": aiBannerContext(2),
    "IMG3-V2": aiBannerContext(3),
    "IMG-competition-A": campaignField(
      "TV-commercial-scene",
      "Banner-competition-A-visual",
    ),
    "IMG-competition-B": campaignField(
      "TV-commercial-scene",
      "Banner-competition-B-visual",
    ),
    "IMG-competition-C": campaignField(
      "TV-commercial-scene",
      "Banner-competition-C-visual",
    ),
    "IMG-competition-D": campaignField(
      "TV-commercial-scene",
      "Banner-competition-D-visual",
    ),
  },
};

/** A database-backed system prompt plus the variation's user input. */
export const systemUser = (systemPromptId, user) => ({
  systemPromptId,
  promptTemplate: [{ role: "user", prompt_template: user }],
});

/**
 * Placeholder syntax for `{{Var}}` expansion.
 *
 * Deliberately narrow — only simple identifiers match, so Handlebars control
 * syntax (`{{#if}}`, `{{/if}}`, `{{~#if}}`, `{{>…}}`, `{{!…}}`) is never
 * touched. Zoltar system prompts are Handlebars templates, so mangling those
 * would be a silent corruption.
 */
const VARIABLE_PATTERN = /\{\{\s*([\w.-]+)\s*\}\}/g;

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

/**
 * Expands `{{Var}}` from `scope`, dropping any line that still contains an
 * unresolved placeholder — a dangling `Tone:` with nothing after it reads worse
 * to an image model than no line at all. Returns `null` when nothing is left.
 */
const interpolate = (text, scope) => {
  if (typeof text !== "string") {
    return text;
  }

  return (
    text
      .split("\n")
      .map((line) => {
        let isResolved = true;

        const expanded = line.replace(VARIABLE_PATTERN, (match, name) => {
          const value = scope?.[name];

          if (!hasValue(value)) {
            isResolved = false;
            return match;
          }

          return value;
        });

        return isResolved ? expanded : null;
      })
      .filter((line) => line !== null)
      .join("\n")
      .trim() || null
  );
};

/**
 * Expands user text in a builder result without interpolating prompt IDs.
 */
export const interpolatePrompt = (result, scope) => {
  if (result && typeof result === "object" && !Array.isArray(result)) {
    return { ...result, promptTemplate: interpolatePrompt(result.promptTemplate, scope) };
  }
  if (Array.isArray(result)) {
    return result.map((part) => ({
      ...part,
      prompt_template: interpolate(part?.prompt_template, scope),
    }));
  }

  return interpolate(result, scope);
};

/**
 * Resolves `variation.values` against the campaign. Each entry is itself a
 * template, so one rule covers both sources without guessing whether a string
 * is a literal or a field name:
 *
 *   - `"warm, optimistic"`      → no placeholders, used as-is
 *   - `"{{TV-commercial-scene-1}}"` → campaign field
 *   - `"for {{Persona-1-audience}}"` → mixed
 *
 * All-or-nothing per entry: if any placeholder inside a value is unresolved the
 * whole entry is dropped, because a half-resolved `"for  in Berlin"` is worse
 * than no value — the prompt line referencing it then drops too.
 */
export const resolveValues = (values, campaign) =>
  Object.fromEntries(
    Object.entries(values ?? {})
      .map(([name, raw]) => [
        name,
        typeof raw === "string" ? interpolate(raw, campaign) : raw,
      ])
      .filter(([, value]) => hasValue(value)),
  );

/** Abstract key visual for a campaign topic (`commonImageAbstract`). */
export const abstractTopicPrompt = ({ customer, visual, visuals }, topic) =>
  systemUser(
    "common-image-abstract",
    `I have a client named: ${customer?.name}
I want to focus on the campaign about: ${topic}
Consider this brand’s marketing communication style, brand voice, style, atmosphere.

${keyVisualsSection({ visual, visuals })}`,
  );

/** Abstract campaign-topic visual with a placement-specific usage note. */
export const campaignTopicPrompt = ({ customer, campaign }, usageNote) =>
  systemUser(
    "common-image-abstract",
    `[Company]: ${customer?.name}
[Campaign-topic]: ${campaign?.["Campaign-topic"]}

${usageNote}`,
  );

/**
 * `Keyframe` / `Keyframe-people` share one long classification prompt, and
 * differ only in whether people may stay in campaign creatives (plus the
 * optional audience note appended for the people variant).
 */
export const keyframePrompt = (campaignCreativeRule, suffix = "") =>
  `Analyze the input image and classify what you received:

- PACKSHOT — product isolated on white/neutral background:
  add an atmospheric environment and lighting that complements the product's colour palette

- POLISHED AD — styled product scene, no people:
  keep the exact background, environment, mood, and lighting — only change the camera angle

- CAMPAIGN CREATIVE — contains real people, text, or slogans:
  ${campaignCreativeRule}
  preserve the product and its branding exactly

- CHARACTER/MASCOT CREATIVE — contains illustrated or CGI brand characters:
  preserve the characters pixel-perfect in appearance, expression, and proportions.
  Place them in a clean, brand-consistent set or environment.
  Remove non-product text only.

- FASHION/APPAREL:
  preserve garments and styling exactly, only adjust composition

---

CRITICAL RULES:

- Treat logos, labels, and branding printed on the product as part of the product → DO NOT remove or alter them
- Only remove external text elements such as headlines, captions, or overlays
- Preserve product details pixel-faithfully (including typography on packaging)
- Do not hallucinate or redesign branding

---

Generate a photorealistic marketing image.
No external text, no overlays, no captions.
Preserve the product and its branding exactly.
Change only the angle and composition.${suffix}`;

export const KEYFRAME_SAFE_MARGIN_PREAMBLE = `Leave generous negative space (copy space) around the main subject on every side — center it, with the subject occupying only about one-quarter of the frame's shorter dimension, real environment filling the rest. For a tall or narrow subject (bottle, mug, tube, glass), leave extra empty space above and below it, even if smaller still. At most one edge may touch the frame border; other sides need clear open space. If a person is in frame, their full head and hands must stay visible. Keep the subject clear of one side where a text/CTA panel may later be added.

Always show a real, depicted environment with visible content — never a flat color, plain gradient, or empty studio backdrop. No borders, vignettes, letterboxing, frames, or stripes at the edges.`;

export const safeKeyframePrompt = () =>
  `${KEYFRAME_SAFE_MARGIN_PREAMBLE}

---

Classify the input image:

- PACKSHOT (product on white/neutral background): add an atmospheric environment and lighting matching the product's palette.
- POLISHED AD (styled scene, no people): keep the exact background, mood, and lighting — only change the camera angle.
- CAMPAIGN CREATIVE (real people, text, or slogans): remove all non-product text, preserve the product and branding exactly.
- CHARACTER/MASCOT (illustrated/CGI characters): preserve characters pixel-perfect; place in a clean, brand-consistent set; remove non-product text only.
- FASHION/APPAREL: preserve garments and styling exactly, only adjust composition.

CRITICAL: logos, labels, and branding on the product are part of the product — never remove or alter them. Only remove external text. Preserve product details pixel-faithfully. Never hallucinate or redesign branding.

Generate a photorealistic marketing image. No external text, overlays, or captions. Preserve the product and branding exactly. Change only angle and composition.`;

export const backgroundVariationPrompt = (environmentNote) =>
  `Generate a fresh environment variation for this subject — one of several background options to choose from. Keep the same composition archetype as the reference: matching camera height/angle and the frame area left open for the subject.

First recognize the background type, then vary accordingly:
- Real location (street, interior, landscape): swap for a different, physically plausible real-world setting where this subject could actually exist — vary place, season, or time of day. Never force an implausible setting (e.g. a vehicle inside a tiny domestic room).
- Stylized effect (particle burst, smoke/dust, splash, light burst): keep the same effect type and energy, vary only material, color, or light — never replace with a literal photographed location.
- Plain studio backdrop/limbo: vary only tone, color, or gradient.

Preserve the reference's own emotional register — if moody, dramatic, or high-energy (common in automotive/sports/action campaigns), keep that energy rather than forcing cheerfulness. Only avoid content that reads as genuinely disturbing or grim beyond the reference's own intent.

If people are present, keep identity unchanged; pose may adapt naturally. Brand elements (logos, liveries, uniforms, signage) must stay exactly unchanged. No text, logos, watermarks.

${environmentNote}`;

export const objectVariationPrompt = (angleNote) =>
  `This is a real product/object that will be shown to actual customers, so brand accuracy is critical: you must not invent, redesign, restyle, recolor, or add any feature, pattern, logo, or detail that isn't already visible in the reference. Treat this strictly as photographing the same physical object from a new angle, not as creative reinterpretation.
Render this exact subject from ${angleNote}, framed as a standard, natural product-photography hero shot (not a close-up, not a cropped fragment) — the whole subject fully visible, at a scale and framing typical of professional catalog/ad photography.
Preserve, with zero deviation: exact shape, proportions, materials, textures, colors, branding, logos, and every visible design detail. Where the new angle reveals a surface not visible in the reference, extend it in a way that is fully consistent with the visible design language and materials — never invent a new pattern, color, or feature there.
Isolate the subject on a soft, uniform studio gradient background using a gentle, low-saturation tint pulled from the subject's own dominant colors (not flat white, not a clashing hue), with a soft, physically plausible contact shadow from a single soft overhead-front light source.
Do not add extra objects, text, logos, or watermarks.`;

/**
 * Templates whose variation prompts never read `visual.labeling` — describing
 * the reference image would only delay generation, so it is skipped entirely.
 */
export const SKIP_IMAGE_LABELING_TEMPLATES = new Set(["one-adobe"]);
